<template>
    <section
        ref="section"
        class="hero col-center">
        <v-avatar
            class="hero__avatar pa-2"
            size="min(50vw, 50vh)">
            <!-- Alternates between the photo and the name every 5 s. -->
            <transition name="slide-fade">
                <v-img
                    v-if="showPhoto"
                    :src="profile.avatar.src"
                    :lazy-src="profile.avatar.lazySrc"
                    :alt="profile.name"
                    height="98%">
                    <template #placeholder>
                        <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center">
                            <v-progress-circular
                                indeterminate
                                color="secondary" />
                        </v-row>
                    </template>
                </v-img>
            </transition>
            <transition name="slide-fade">
                <div
                    v-if="!showPhoto"
                    class="hero__name secondary--text font-weight-bold text-md-h3">
                    {{ profile.name }}
                </div>
            </transition>
        </v-avatar>
        <div class="text-h3 secondary--text text-center font-weight-bold px-3 my-5">{{ profile.headline }}</div>
        <!-- Smaller on phones/tablets so the longest tagline ("Artificial Intelligence") stays on one line. -->
        <div class="hero__tagline text-subtitle-1 text-sm-h6 text-md-h5 font-weight-regular secondary--text px-3">{ {{ tagline }}<span class="cursor">_</span>}</div>
    </section>
</template>

<script>
import { ref, computed } from 'vue';
import { profile } from '@/data/profile';
import { useAutoCycle } from '@/composables/useAutoCycle';
import { useCodeScreenSight } from '@/composables/useCodeScreenSight';
import { useScrambleLoop } from '@/composables/useTextScramble';

export default {
    name: 'HeroSection',
    setup() {
        const section = ref(null);
        const { index } = useAutoCycle(2, 5000);
        const tagline = useScrambleLoop(profile.taglines);
        // The code screen shows through the hero, until the resume scrolls over it.
        useCodeScreenSight(section);

        return {
            section,
            profile,
            tagline,
            showPhoto: computed(() => index.value === 0),
        };
    },
};
</script>

<style scoped>
.hero {
    height: 100vh;
    filter: url(#turbulence);
}
.hero__avatar {
    border-width: min(3vh, 3vw);
    border-color: var(--v-secondary-base);
    border-style: solid;
    background: linear-gradient(to right, var(--v-backdrop-start-base) -100%, var(--v-backdrop-end-base) 200%);
}
.hero__name {
    position: absolute;
}
/* Monospace so the scrambled glyphs don't change the tagline's width. */
.hero__tagline {
    font-family: ui-monospace, 'Cascadia Mono', 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace !important;
}

.slide-fade-enter-active {
    transition: all 0.7s cubic-bezier(0.11, 1.04, 0.8, 0.99);
}
.slide-fade-leave-active {
    transition: all 0.5s cubic-bezier(0.68, 0.04, 1, 0.7);
}
.slide-fade-enter {
    transform: translateX(25vw) scale(0.8);
    filter: blur(10px);
    opacity: 0;
}
.slide-fade-leave-to {
    transform: translateX(-100vw) scale(0.97);
    opacity: 0;
}
</style>
